/* eslint-disable react-refresh/only-export-components */
import { useEffect, useState } from 'react';
import { Alert, Button } from 'antd';
import { ArrowLeftOutlined, ArrowRightOutlined } from '@ant-design/icons';
import { getStatus } from '@/api/dataroom';
import type { JourneyPage, JourneyPages, StatusResponse, Task, TaskStatus } from '@/api/types';
import { useI18n } from '@/i18n/useI18n';
import { Brand } from './Brand';
import { OverallBanner } from './OverallBanner';
import { StatusStepper } from './StatusStepper';
import { DeclarationTask } from './tasks/DeclarationTask';
import { erasedOr } from '@/lib/erased';

/**
 * The studio's welcome, terms and final pages (OL-23). One component per page, a closed list of variants,
 * and only plain text. With no pages authored, the room never gets here.
 */

export type PageStage = 'welcome' | 'terms' | 'final' | 'tasks';
export interface PagesPassed {
  welcome: boolean;
  notice: boolean;
  final: boolean;
}

/** Which page is due. It is decided from the server's task statuses, so a reload and a re-request both land right. */
export function pageStage(
  pages: JourneyPages | null | undefined,
  tasks: Task[],
  passed: PagesPassed,
): PageStage {
  if (!pages) return 'tasks';
  const untouched = tasks.every((x) => x.status === 'PENDING');
  if (pages.welcome && untouched && !passed.welcome) return 'welcome';
  if (pages.terms?.variant === 'CONSENT') {
    // The gate holds this to a required first DECLARATION. If that is ever not so, the step list still serves it.
    if (tasks[0]?.kind === 'DECLARATION' && tasks[0].status === 'PENDING') return 'terms';
  } else if (pages.terms && untouched && !passed.notice) {
    return 'terms';
  }
  const finished =
    tasks.some((x) => x.status !== 'PENDING') &&
    tasks.every((x) => x.optional || x.status !== 'PENDING');
  if (pages.final && finished && !passed.final) return 'final';
  return 'tasks';
}

export function WelcomePage({
  page,
  tasks,
  onStart,
}: {
  page: JourneyPage;
  tasks: Task[];
  onStart: () => void;
}) {
  const { t, dir } = useI18n();
  const hero = page.variant === 'HERO';
  return (
    <section className={`dr-page${hero ? ' dr-page--hero' : ''}`} data-testid="page-welcome" data-variant={page.variant}>
      {hero && <Brand showTitle={false} />}
      <h1 className="dr-page__h">{page.heading ?? t('pages.welcome.heading')}</h1>
      <p className="dr-page__body">{page.body ?? t('pages.welcome.body')}</p>
      {page.variant === 'CHECKLIST' && (
        <>
          <h2 className="dr-page__sub">{t('pages.welcome.stepsHeading')}</h2>
          <ol className="dr-page__list" data-testid="page-welcome-steps">
            {tasks.map((x) => (
              <li key={x.taskId}>{erasedOr(x.title, t('common.erased'))}</li>
            ))}
          </ol>
        </>
      )}
      <div className="dr-page__actions">
        <Button
          type="primary"
          size="large"
          block
          icon={dir === 'rtl' ? <ArrowLeftOutlined /> : <ArrowRightOutlined />}
          iconPosition="end"
          onClick={onStart}
          data-testid="page-welcome-start"
        >
          {page.label ?? t('pages.welcome.start')}
        </Button>
      </div>
    </section>
  );
}

/**
 * NOTICE is a page with a continue button: it records nothing and is not consent. CONSENT is the journey's
 * first step, in DeclarationTask itself, so the text, the hash and the record are the declaration's own.
 */
export function TermsPage({
  page,
  declaration,
  onContinue,
  onStatus,
  onRefresh,
}: {
  page: JourneyPage;
  declaration: Task | undefined;
  onContinue: () => void;
  onStatus: (taskId: string, status: TaskStatus) => void;
  onRefresh: () => void;
}) {
  const { t, dir } = useI18n();
  const consent = page.variant === 'CONSENT' && declaration;
  return (
    <section className="dr-page" data-testid="page-terms" data-variant={page.variant}>
      <h1 className="dr-page__h">{page.heading ?? t('pages.terms.heading')}</h1>
      {page.body && (
        <p className="dr-page__body dr-legal" data-testid="page-terms-body">
          {page.body}
        </p>
      )}
      {consent ? (
        <>
          {declaration.reopenNote && <Alert type="warning" showIcon message={declaration.reopenNote} />}
          <DeclarationTask
            task={declaration}
            onStatus={(status) => onStatus(declaration.taskId, status)}
            onRefresh={onRefresh}
          />
        </>
      ) : (
        <div className="dr-page__actions">
          <Button
            type="primary"
            size="large"
            block
            icon={dir === 'rtl' ? <ArrowLeftOutlined /> : <ArrowRightOutlined />}
            iconPosition="end"
            onClick={onContinue}
            data-testid="page-terms-continue"
          >
            {page.label ?? t('pages.terms.continue')}
          </Button>
        </div>
      )}
    </section>
  );
}

/** Says the room has what it asked for, and nothing about what will come of it: tipping-off safe by what it can show. */
export function FinalPage({
  page,
  onBack,
  onViewStatus,
}: {
  page: JourneyPage;
  onBack: () => void;
  onViewStatus: () => void;
}) {
  const { t } = useI18n();
  const [status, setStatus] = useState<StatusResponse | null>(null);
  const withStatus = page.variant === 'STATUS';
  useEffect(() => {
    if (!withStatus) return undefined;
    let current = true;
    getStatus()
      .then((res) => {
        if (current) setStatus(res);
      })
      .catch(() => {});
    return () => {
      current = false;
    };
  }, [withStatus]);
  return (
    <section className="dr-page" data-testid="page-final" data-variant={page.variant}>
      <h1 className="dr-page__h">{page.heading ?? t('pages.final.heading')}</h1>
      <p className="dr-page__body">{page.body ?? t('pages.final.body')}</p>
      {page.variant === 'NEXT_STEPS' && (page.nextSteps?.length ?? 0) > 0 && (
        <>
          <h2 className="dr-page__sub">{t('pages.final.nextStepsHeading')}</h2>
          <ol className="dr-page__list" data-testid="page-final-steps">
            {page.nextSteps?.map((step) => (
              <li key={step}>{step}</li>
            ))}
          </ol>
        </>
      )}
      {status && (
        <div data-testid="page-final-status">
          <OverallBanner status={status.overallStatus} />
          <StatusStepper steps={status.steps} />
        </div>
      )}
      <div className="dr-page__actions">
        {!withStatus && (
          <Button size="large" block onClick={onViewStatus} data-testid="page-final-status-link">
            {t('pages.final.viewStatus')}
          </Button>
        )}
        <Button size="large" block type="text" onClick={onBack} data-testid="page-final-back">
          {t('pages.final.backToSteps')}
        </Button>
      </div>
    </section>
  );
}
